import { useEffect, useRef, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { paths, type TechLocation } from "@clevis/shared-types";
import { Card } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { loadGoogleMaps, type MapsLib } from "../lib/googleMaps";

// Where everyone is right now. One marker per tech, straight from
// techLocations (a single overwritten doc per tech — no trail). Markers go
// gray once a position is older than 3 minutes.

const STALE_MS = 3 * 60_000;

interface Props {
  tenantId: string;
  memberName: (uid: string) => string;
  home?: { lat: number; lng: number } | null;
}

type Pos = TechLocation & { uid: string };

/* eslint-disable @typescript-eslint/no-explicit-any */
export function LiveTechMap({ tenantId, memberName, home }: Props) {
  const holder = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const libRef = useRef<MapsLib | null>(null);
  const markers = useRef(new Map<string, any>());
  const [positions, setPositions] = useState<Pos[]>([]);
  const [error, setError] = useState("");
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(collection(db, paths.tenantTechLocations(tenantId)), (snap) =>
      setPositions(snap.docs.map((d) => ({ uid: d.id, ...(d.data() as TechLocation) }))),
    );
  }, [tenantId]);

  // Staleness is time-based, so refresh the gray state once a minute.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let dead = false;
    void loadGoogleMaps()
      .then((lib) => {
        if (dead || !holder.current || mapRef.current) return;
        libRef.current = lib;
        mapRef.current = new lib.Map(holder.current, {
          center: home ?? { lat: 39.5, lng: -98.35 },
          zoom: home ? 10 : 4,
          mapId: "DEMO_MAP_ID",
          disableDefaultUI: true,
          zoomControl: true,
        });
      })
      .catch((e) => setError((e as Error).message));
    return () => {
      dead = true;
    };
  }, [home]);

  useEffect(() => {
    const g = libRef.current;
    const map = mapRef.current;
    if (!map || !g) return;
    const seen = new Set<string>();
    const bounds = new g.LatLngBounds();
    let any = false;
    for (const p of positions) {
      if (!p.onDuty) continue;
      seen.add(p.uid);
      const stale = Date.now() - p.updatedAt > STALE_MS;
      const name = memberName(p.uid);
      const initials = name
        .split(/\s+/)
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
      let m = markers.current.get(p.uid);
      if (!m) {
        const el = document.createElement("div");
        el.className = "clevis-tech-pin";
        m = new g.AdvancedMarkerElement({ map, content: el });
        markers.current.set(p.uid, m);
      }
      (m.content as HTMLElement).textContent = initials || "?";
      (m.content as HTMLElement).title = `${name}${stale ? " (stale)" : ""}`;
      (m.content as HTMLElement).style.cssText =
        `display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:50%;` +
        `font:600 11px sans-serif;color:#fff;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);` +
        `background:${stale ? "#9aa7ad" : "#DF8048"}`;
      m.position = { lat: p.lat, lng: p.lng };
      bounds.extend({ lat: p.lat, lng: p.lng });
      any = true;
    }
    for (const [uid, m] of markers.current) {
      if (!seen.has(uid)) {
        m.map = null;
        markers.current.delete(uid);
      }
    }
    if (any && !home) map.fitBounds(bounds, 48);
  }, [positions, memberName, home]);

  const onDuty = positions.filter((p) => p.onDuty);
  return (
    <Card className="p-0">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-xs font-semibold text-ink">Live map</span>
        <span className="text-[11px] text-steel">
          {onDuty.length === 0 ? "No one is sharing location" : `${onDuty.length} on duty`}
        </span>
      </div>
      {error ? (
        <div className="px-3 pb-3 text-xs text-danger">{error}</div>
      ) : (
        <div ref={holder} className="h-64 w-full rounded-b-xl" />
      )}
    </Card>
  );
}
